import { SvIds } from 'store/src/constants/static-table-sv-id'
import { useAssetsStore, useStaticTableDataStore } from 'store'
import { useHandleMoreList } from '../use-handle-more-list'
import { getAssetsPageRoute, PageRoutes, UserCenterRoutes } from '@/constants/PageRoutes'
import { useI18n } from 'i18n-utils'
import { UiUtils } from 'ui-components'
import { ASSET_TABS, AssetType } from '@/project-config'

export const AssetTabs = ASSET_TABS

export const useAssetFundAssetListComponent = (isHolding = false) => {
  const assetsStore = useAssetsStore()
  const staticTableStore = useStaticTableDataStore()
  const currentTab = ref(AssetType.Digital)
  const digitalMoreData = useHandleMoreList<ApiResponse.RemainAssetListItem>({
    pageSize: 15,
    page: 1,
  })
  const fiatMoreData = useHandleMoreList<ApiResponse.RemainAssetListItem>({
    pageSize: 15,
    page: 1,
  })
  const router = useRouter()
  const { t } = useI18n()
  const searchValue = ref('')
  const hideSmallAmount = ref(false)

  const itemList = computed(() => {
    return staticTableStore.staticTableDataMap[SvIds.StaticItemV2]
  })

  const getIsDigital = (itemId: number) => {
    return itemList.value?.[itemId]?.type === AssetType.Digital
  }

  const getIsFiat = (itemId: number) => {
    return itemList.value?.[itemId]?.type === AssetType.Fiat
  }

  const assetList = computed(() => {
    return assetsStore.assets?.remain.list.filter((item) => {
      if (hideSmallAmount.value) {
        return item.all.isGreaterThan(0)
      }
      return true
    })
  })

  const sortByOver = (a: ApiResponse.RemainAssetListItem, b: ApiResponse.RemainAssetListItem) => {
    return a.over.subtractStr(b.over).isGreaterThan(0) ? -1 : 1
  }

  /** 数字货币 */
  const digitalCurrencyList = computed(() => {
    return assetList.value
      ?.filter((item) => getIsDigital(item.itemId))
      .sort((a, b) => a.orderBy - b.orderBy)
      .sort(sortByOver)
  })
  /** 法币 */
  const fiatCurrencyList = computed(() => {
    return assetList.value
      ?.filter((item) => getIsFiat(item.itemId))
      .sort((a, b) => a.orderBy - b.orderBy)
      .sort(sortByOver)
  })

  const currentAssetList = computed(() => {
    let list = currentTab.value === AssetType.Digital ? digitalCurrencyList.value : fiatCurrencyList.value

    if (hideSmallAmount.value) {
      list = list?.filter((item) => item.valuation.isGreaterThan(0))
    }

    if (searchValue.value) {
      list = list?.filter((item) => {
        const itemInfo = staticTableStore.getItemInfoByItemId(item.itemId)
        return itemInfo?.itemName.toUpperCase().includes(searchValue.value.toUpperCase())
      })
    }

    if (isHolding) {
      list = list?.filter((item) => item.all.isGreaterThan(0))
    }

    return list
  })

  watchEffect(() => {
    if (currentTab.value === AssetType.Digital) {
      digitalMoreData.init(currentAssetList.value || [])
    } else {
      fiatMoreData.init(currentAssetList.value || [])
    }
  })

  /** 币种名称排序 */
  const sortMethodByItemName = (a: ApiResponse.RemainAssetListItem, b: ApiResponse.RemainAssetListItem) => {
    const itemA = staticTableStore.getItemInfoByItemId(a.itemId)
    const itemB = staticTableStore.getItemInfoByItemId(b.itemId)
    if (!itemA || !itemB) return 0
    return itemA.itemName.localeCompare(itemB.itemName)
  }

  /** 全部资产排序 */
  const sortMethodByAll = (a: ApiResponse.RemainAssetListItem, b: ApiResponse.RemainAssetListItem) => {
    return a.all.subtractStr(b.all).toNumber()
  }

  /** 可用资产排序 */
  const sortMethodByOver = (a: ApiResponse.RemainAssetListItem, b: ApiResponse.RemainAssetListItem) => {
    return a.over.subtractStr(b.over).toNumber()
  }

  /** 冻结资产排序 */
  const sortMethodByLock = (a: ApiResponse.RemainAssetListItem, b: ApiResponse.RemainAssetListItem) => {
    return a.lock.subtractStr(b.lock).toNumber()
  }

  /** 估值排序 */
  const sortMethodByValuation = (a: ApiResponse.RemainAssetListItem, b: ApiResponse.RemainAssetListItem) => {
    return a.valuation.subtractStr(b.valuation).toNumber()
  }

  /** 币币列表 */
  const coinList = computed(() => {
    if (!staticTableStore.staticTableDataMap[SvIds.StaticSymbol]) return []
    return Object.values(staticTableStore.staticTableDataMap[SvIds.StaticSymbol])
  })

  /** 跳转交易 */
  const onTrade = (item: ApiResponse.RemainAssetListItem, isNewPageOpen = true) => {
    const coin = coinList.value.find((coin) => coin.coinFrom === item.itemId)
    if (!coin) {
      UiUtils.showWarning(t('暂未支持该币种交易'))
      return
    }
    if (isNewPageOpen) {
      window.open(`${PageRoutes.SpotTrade}?symbol=${coin.symbolId}`, '_blank')
    } else {
      router.push({
        path: PageRoutes.SpotTrade,
        query: {
          symbol: coin.symbolId,
        },
      })
    }
  }

  const onRecharge = (item: ApiResponse.RemainAssetListItem) => {
    router.push({
      path: getAssetsPageRoute(UserCenterRoutes.Recharge),
      query: {
        itemId: item.itemId,
      },
    })
  }

  const onWithdraw = (item: ApiResponse.RemainAssetListItem) => {
    router.push({
      path: getAssetsPageRoute(UserCenterRoutes.Withdraw),
      query: {
        itemId: item.itemId,
      },
    })
  }

  return {
    currentTab,
    digitalMoreData,
    fiatMoreData,
    currentAssetList,
    sortMethodByItemName,
    sortMethodByAll,
    sortMethodByOver,
    sortMethodByLock,
    sortMethodByValuation,
    onTrade,
    searchValue,
    hideSmallAmount,
    onRecharge,
    onWithdraw,
    digitalCurrencyList,
    fiatCurrencyList,
  }
}
